import { useLayoutEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import type { Series } from '../../../services/source-page-api';
import { formatPlain } from '../../../utils/temporal';

/**
 * Validated in the dataviz palette checker (ordinal mode) against both themes —
 * do not substitute by eye.
 */
const SERIES_COLORS = {
  light: { current: 0x1fa5dc, prior: 0x93b4c6 },
  dark: { current: 0x4fc1ed, prior: 0x4a7b92 },
} as const;

/**
 * `rendering/theme.ts` applies am5themes_Dark unconditionally and its tokens are
 * hardcoded dark hexes, so it cannot serve a page that must work in both themes.
 * This reads the live semantic tokens instead.
 */
function cssColor(name: string, fallback: number): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return fallback;
  const [h, s, l] = raw.split(/\s+/);
  const probe = document.createElement('div');
  probe.style.color = `hsl(${h} ${s} ${l})`;
  document.body.appendChild(probe);
  const rgb = getComputedStyle(probe).color.match(/\d+/g);
  probe.remove();
  if (!rgb) return fallback;
  return (Number(rgb[0]) << 16) | (Number(rgb[1]) << 8) | Number(rgb[2]);
}

interface Props {
  series: Series;
  isDark: boolean;
  formatValue: (n: number) => string;
}

export default function SeriesChart({ series, isDark, formatValue }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const root = am5.Root.new(ref.current);
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo?.dispose();

    const palette = isDark ? SERIES_COLORS.dark : SERIES_COLORS.light;
    const textMuted = cssColor('--muted-foreground', isDark ? 0x8899aa : 0x616161);
    const border = cssColor('--border', isDark ? 0x1e2d3d : 0xe5e5e5);
    const card = cssColor('--card', isDark ? 0x131c27 : 0xffffff);

    const theme = am5.Theme.new(root);
    theme.rule('Label').setAll({ fill: am5.color(textMuted), fontSize: 11 });
    theme.rule('Grid').setAll({ stroke: am5.color(border), strokeOpacity: 0.6 });
    theme.rule('RoundedRectangle', ['tooltip', 'background']).setAll({
      fill: am5.color(card),
      fillOpacity: 0.98,
      stroke: am5.color(border),
    });
    root.setThemes([am5themes_Animated.new(root), theme]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        paddingLeft: 0,
        paddingRight: 8,
        paddingTop: 8,
      }),
    );

    const data = series.buckets.map((b) => ({
      label: bucketLabel(b.start, series.grain),
      current: b.current,
      prior: b.prior,
      partial: b.partial,
      shortfall: b.shortfall,
    }));

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'label',
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 24,
          cellStartLocation: 0.12,
          cellEndLocation: 0.88,
        }),
      }),
    );
    xAxis.get('renderer').grid.template.setAll({ visible: false });
    xAxis.data.setAll(data);

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        renderer: am5xy.AxisRendererY.new(root, { minGridDistance: 36 }),
      }),
    );
    yAxis.get('renderer').labels.template.adapters.add('text', (_t, target) => {
      const v = (target.dataItem as { get?: (k: string) => number })?.get?.('value');
      return typeof v === 'number' ? formatValue(v) : _t;
    });

    function addSeries(name: string, field: 'current' | 'prior', color: number) {
      const s = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name,
          xAxis,
          yAxis,
          valueYField: field,
          categoryXField: 'label',
        }),
      );
      s.columns.template.setAll({
        fill: am5.color(color),
        stroke: am5.color(color),
        width: am5.percent(84),
        cornerRadiusTL: 3,
        cornerRadiusTR: 3,
        tooltipText: '{name}: {valueY}',
      });

      // The in-progress bucket is dimmed AND capped with a solid line — the
      // state must never rest on colour alone.
      if (field === 'current') {
        s.columns.template.adapters.add('fillOpacity', (v, target) =>
          (target.dataItem?.dataContext as { partial?: boolean })?.partial ? 0.45 : 1,
        );
        s.columns.template.adapters.add('strokeWidth', (v, target) =>
          (target.dataItem?.dataContext as { partial?: boolean })?.partial ? 2 : 0,
        );
        s.columns.template.adapters.add('tooltipText', (t, target) => {
          const ctx = target.dataItem?.dataContext as { partial?: boolean; shortfall?: string };
          return ctx?.partial && ctx.shortfall
            ? `{name}: {valueY}\n[bold]In progress[/] — ${ctx.shortfall}`
            : t;
        });
      }
      s.data.setAll(data);
      s.appear(600);
      return s;
    }

    addSeries(series.currentLabel, 'current', palette.current);
    if (series.priorAvailable) addSeries(series.priorLabel, 'prior', palette.prior);

    chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none' }));
    chart.get('cursor')?.lineX.setAll({ visible: false });
    chart.get('cursor')?.lineY.setAll({ visible: false });

    return () => root.dispose();
  }, [series, isDark, formatValue]);

  return <div ref={ref} className="w-full h-[300px]" role="img" aria-label={`${series.metricLabel} by ${series.grain}`} />;
}

/**
 * `start` is a plain wall clock already expressed in the business timezone, so it
 * is rendered verbatim — converting it would re-zone it into the viewer's clock
 * and slide every label by the UTC offset.
 */
function bucketLabel(start: string, grain: Series['grain']): string {
  if (grain === 'hour') return formatPlain(start, { hour: 'numeric' });
  if (grain === 'month') return formatPlain(start, { month: 'short' });
  return formatPlain(start, { month: 'short', day: 'numeric' });
}
